/**
 * Authentication Service
 * Orchestrates authentication flow using the BackendAuthProvider.
 * All environments (local, develop, staging, production) use real JWT authentication.
 */

import { useAuthStore } from './auth-store';
import { forgetSession, leaveSession } from '../../lib/session-end';
import { BackendAuthProvider } from './backend-auth-provider';
import type { AuthProvider } from './auth-provider';
import type { BackendLoginResponse } from './backend-auth-provider';
import type { LoginCredentials } from './auth-provider';
import type { PortalUser } from '../../api/portal-user';
import { familyDeviceApi } from '../../api/family-device/api';

const LOCAL_DEV_PASSWORD = '12345678';

class AuthService {
  private provider: AuthProvider;
  private operationRevision = 0;
  private sessionChangePending = false;
  // A request can fail without replacing a session. Logout follows this
  // committed generation rather than the revision of the latest request.
  private sessionRevision = 0;
  private pendingLogout: { sessionRevision: number; promise: Promise<void> } | null = null;

  constructor() {
    this.provider = new BackendAuthProvider();
    this.initializeSession();
  }

  private async initializeSession(): Promise<void> {
    const revision = this.operationRevision;
    const user = this.provider.getCurrentUser();
    if (user) {
      const isValid = await this.provider.validateSession();
      if (revision !== this.operationRevision) return;
      if (isValid) {
        // Re-read after the await. `validateSession()` fetches /auth/me, merges
        // the fresh fields and persists them — pushing the snapshot taken
        // *before* it means the refresh reaches localStorage and never reaches
        // the store, leaving it one boot behind. Latent until this change, since
        // name/role never move mid-session; with the profile photo it means
        // removing your photo brings it back on the next reload.
        const refreshed = this.provider.getCurrentUser();
        if (!refreshed) return;
        useAuthStore.getState().setUser(refreshed);
        console.log('[AuthService] Session restored for:', refreshed.name);
      } else {
        await this.logout({ expired: true });
      }
    }
  }

  /** Each user action owns its continuations, including same-token sign-ins. */
  private beginOperation(pending = false): number {
    this.provider.cancelPendingRequests();
    this.sessionChangePending = pending;
    return ++this.operationRevision;
  }

  /** Adopt a locally-made change to the signed-in user into store and storage. */
  persistCurrentUser(user: PortalUser): void {
    // Metadata arriving from a prior mutation must not cancel a sign-in or
    // sign-out, or resurrect an identity that has already left this tab.
    if (this.sessionChangePending || this.pendingLogout?.sessionRevision === this.sessionRevision ||
      this.provider.getCurrentUser()?.id !== user.id) return;
    this.beginOperation();
    this.provider.updateCurrentUser(user);
    useAuthStore.getState().setUser(user);
    useAuthStore.getState().setIsLoading(false);
  }

  async login(credentials: LoginCredentials): Promise<void> {
    const revision = this.beginOperation(true);
    useAuthStore.getState().setIsLoading(true);
    useAuthStore.getState().setError(null);

    try {
      const departing = this.pendingLogout;
      if (departing?.sessionRevision === this.sessionRevision) await departing.promise;
      if (revision !== this.operationRevision) return;
      // The departing session's teardown resets all stores, including busy.
      useAuthStore.getState().setIsLoading(true);
      const result = await this.provider.login(credentials);
      if (revision !== this.operationRevision) return;

      if (result.success && result.session) {
        this.publishLoginResponse(result.session);
      } else {
        useAuthStore.getState().setError(result.error ?? 'invalid_credentials');
        console.error('[AuthService] Login failed:', result.error);
      }
    } catch (error) {
      if (revision !== this.operationRevision) return;
      // The provider already converts an unreachable server into a
      // network_error result, so anything thrown here is unexpected — still a
      // failure to complete the request, not a rejected password.
      useAuthStore.getState().setError('network_error');
      console.error('[AuthService] Login error:', error);
    } finally {
      if (revision === this.operationRevision) {
        this.sessionChangePending = false;
        useAuthStore.getState().setIsLoading(false);
      }
    }
  }

  /**
   * Signs out, and empties everything the session filled in this tab
   * (forgetSession, lib/session-end.ts) — the sign-in, the queries, the
   * moderator's call and transcript, a question still asked, the toasts — so
   * the next user to sign in here sees none of it. Without the queries'
   * clearing, signing out of student 1 and in as student 2 showed student 1's
   * portfolio until it went stale.
   *
   * A user who signs out still holds a working token, so what the session
   * must still say goes first (leaveSession): a moderator's call tells the
   * player's match it is over. A session that expired says nothing: its token
   * is refused, and a refusal would sign it out again.
   */
  logout({ expired = false }: { expired?: boolean } = {}): Promise<void> {
    const revision = this.beginOperation(true);
    if (this.pendingLogout?.sessionRevision === this.sessionRevision) {
      // Reuse the notification, but a repeated explicit sign-out still
      // invalidates any sign-in queued since the first request.
      return this.pendingLogout.promise.finally(() => {
        if (revision === this.operationRevision) this.sessionChangePending = false;
      });
    }
    const departing = { sessionRevision: this.sessionRevision, promise: Promise.resolve() };
    this.pendingLogout = departing;
    departing.promise = this.finishLogout(departing.sessionRevision, expired).finally(() => {
      if (this.pendingLogout === departing) this.pendingLogout = null;
      if (revision === this.operationRevision) this.sessionChangePending = false;
    });
    return departing.promise;
  }

  /** Complete the requested session's departure, regardless of failed new attempts. */
  private async finishLogout(sessionRevision: number, expired: boolean): Promise<void> {
    console.log('[AuthService] Logging out...');
    try {
      if (!expired) await leaveSession();
    } catch (error) {
      console.error('[AuthService] Sign-out notification error:', error);
    }
    // A replacement already ended this session before publishing its identity.
    if (sessionRevision !== this.sessionRevision) return;
    this.sessionRevision++;
    try {
      const clearing = this.provider.logout();
      forgetSession();
      await clearing;
      console.log('[AuthService] Logout successful');
    } catch (error) {
      console.error('[AuthService] Logout error:', error);
    }
  }

  /** Log in as a specific local dev user (admin / moderator / student). */
  async loginAsLocalDevUser(username: string): Promise<void> {
    console.log(`[AuthService] Local dev login as: ${username}`);
    await this.login({ username, password: LOCAL_DEV_PASSWORD });
  }

  /**
   * Ends the previous session's state and publishes a verified password login
   * or externally obtained session (guardian signup or family profile login).
   */
  adoptLoginResponse(data: BackendLoginResponse): void {
    this.beginOperation();
    this.publishLoginResponse(data);
  }

  /** Keep publication failures owned by the sign-in that obtained this session. */
  private publishLoginResponse(data: BackendLoginResponse): void {
    // This is the only service path that publishes a successful sign-in.
    // Clear old data before the new token/user can make it visible to a screen.
    forgetSession();
    const user = this.provider.adoptSession(data);
    // A failed adoption has not replaced the departing session: its pending
    // sign-out must still clear any credentials left by the failed write.
    this.sessionRevision++;
    useAuthStore.getState().setUser(user);
    useAuthStore.getState().setError(null);
    useAuthStore.getState().setIsLoading(false);
    console.log('[AuthService] Session adopted for:', user.name);
  }

  /**
   * Netflix-style student login from a linked family device: the child picked
   * their profile on the picker; the device token is the credential. Callers
   * may navigate only when this operation actually adopted the session.
   */
  async loginWithProfile(studentUserId: string): Promise<{ success: boolean }> {
    const revision = this.beginOperation(true);
    useAuthStore.getState().setIsLoading(true);
    useAuthStore.getState().setError(null);
    try {
      const departing = this.pendingLogout;
      if (departing?.sessionRevision === this.sessionRevision) await departing.promise;
      if (revision !== this.operationRevision) return { success: false };
      useAuthStore.getState().setIsLoading(true);
      const data = await familyDeviceApi.profileLogin(studentUserId);
      if (revision !== this.operationRevision) return { success: false };
      this.publishLoginResponse(data);
      return { success: true };
    } catch (error) {
      if (revision !== this.operationRevision) return { success: false };
      useAuthStore.getState().setError('network_error');
      console.error('[AuthService] Profile login error:', error);
      return { success: false };
    } finally {
      if (revision === this.operationRevision) {
        this.sessionChangePending = false;
        useAuthStore.getState().setIsLoading(false);
      }
    }
  }

  /**
   * "Switch profile" on a family device: ends only the current session (the
   * family device token is stored under its own key and survives) and returns
   * to the root — which renders the profile picker automatically on linked
   * devices (in local dev, the hub, whose family button reopens the picker).
   */
  async switchProfile(): Promise<void> {
    await this.logout();
    window.location.href = window.location.pathname;
  }

  getCurrentUser() {
    return this.provider.getCurrentUser();
  }
}

export const authService = new AuthService();
